import {
  createAction, // @sieradz - launch actions and change application state without communicating with the API
  createAsyncThunk,
  createSelector,
  createSlice} from '@reduxjs/toolkit'
import { map, pathOr, pipe } from 'ramda'
import { RootState } from 'store'

import { AuthRepository } from 'services/repositories'
import RolesRepository from 'services/repositories/RolesRepository'
import {
  LoginPayload,
  ResetPasswordPayload,
  SetPasswordPayload,
  SetPolicyAcceptance
} from 'types/auth'
import { mapUserPermissions } from 'types/lib/auth'
import { IRole } from 'types/roles'
import { IOptionSelectF } from 'types/SelectF'
import { IAuthState } from 'types/slices'
import {
  ICountryPermissions,
  IUserInfo,
  IUserTokens,
  LoadingStatus,
  TLoadingStatus} from 'types/user'
import { handleApiErrors } from 'utils/errors'
import { KEYS, localStorageService } from 'utils/localStorage'
import { PATHS } from 'utils/paths'
import { isNotNilOrEmpty } from 'utils/ramda'

const initialState: IAuthState = {
  status: 'initial',
  user: {
    accessToken: '',
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    fullName: '',
    createdAt: '',
    defaultCountry: 'PL',
    countryPermissions: [],
    cookiesFlag: undefined,
    privacyPolicyFlag: undefined,
    newsletterFlag: undefined
  },
  userPermissions: {},
  userTokens: [
    {
      id: '',
      userId: '',
      tokenType: 'ai_translation',
      currentUsage: null,
      maxLimit: 100,
      resetDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: '',
      userId: '',
      tokenType: 'ai_generation',
      currentUsage: null,
      maxLimit: 100,
      resetDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ],
  tokensStatus: 'initial'
}

export const types = {
  login: 'auth/login',
  logout: 'auth/logout',
  resetPassword: 'auth/resetPassword',
  setPassword: 'auth/setPassword',
  getProfile: 'auth/getProfile',
  setUserToken: 'auth/setUserToken',
  refreshAuthToken: 'auth/refreshAuthToken',
  setPolicyAcceptance: 'auth/setPolicyAcceptance'
} as const

export const login = createAsyncThunk(
  types.login,
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      localStorageService.remove(KEYS.token)
      const response = await AuthRepository.login(payload)

      localStorageService.set(KEYS.token, response.data.accessToken)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const logout = createAsyncThunk(types.logout, () => {
  localStorageService.remove(KEYS.token)
  localStorageService.remove(KEYS.menu) // @sieradz - on logout remove memory of opened left menu
  window.location.href = PATHS.login
})

export const getProfile = createAsyncThunk(
  types.getProfile,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthRepository.getProfile()
      const permissions = await RolesRepository.getAllPermissions()

      return { data: response.data, permissions: permissions.data }
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const resetPassword = createAsyncThunk(
  types.resetPassword,
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      const response = await AuthRepository.resetPassword(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)
export const setPassword = createAsyncThunk(
  types.setPassword,
  async (payload: SetPasswordPayload, { rejectWithValue }) => {
    try {
      const response = await AuthRepository.setPassword(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const refreshAuthToken = createAsyncThunk(
  types.refreshAuthToken,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthRepository.refreshAuthToken()

      localStorageService.set(KEYS.token, response.data.accessToken)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const setUserToken = createAction(types.setUserToken, () => {
  const localStorageUserToken = localStorageService.get(KEYS.token)

  if (
    isNotNilOrEmpty(localStorageUserToken) &&
    typeof localStorageUserToken === 'string'
  ) {
    return { payload: localStorageUserToken }
  }

  return { payload: '' }
})

export const setPolicyAcceptance = createAsyncThunk(
  types.setPolicyAcceptance,
  async (payload: SetPolicyAcceptance, { rejectWithValue }) => {
    try {
      const response = await AuthRepository.setPolicyAcceptance(payload)

      localStorageService.set(KEYS.token, response.data.accessToken)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.user = {
          ...state.user,
          ...action.payload,
          fullName: `${action.payload.firstName} ${action.payload.lastName}`
        }
      })
      .addCase(login.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getProfile.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        const profileData = action.payload.data as typeof action.payload.data & {
          user?: { role?: IRole }
        }
        const roleFromProfile =
          profileData.role ?? profileData.user?.role

        state.user = {
          ...state.user,
          ...profileData,
          countryPermissions: profileData.countryPermissions,
          fullName: `${profileData.firstName} ${profileData.lastName}`,
          ...(roleFromProfile ? { role: roleFromProfile } : {})
        }

        const { permissions } = action.payload
        const role = roleFromProfile

        const rolePermissions = role?.rolePermissions?.map(
          (rp: any) => rp.permission
        )

        const userPermissions = mapUserPermissions(rolePermissions, permissions)

        state.userPermissions = userPermissions
      })
      .addCase(getProfile.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err)
      })
      .addCase(resetPassword.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(setPassword.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(setUserToken, (state, action) => {
        state.user = {
          ...state.user,
          accessToken: action.payload
        }
      })
      .addCase(refreshAuthToken.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(refreshAuthToken.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.user = {
          ...state.user,
          ...action.payload,
          fullName: `${action.payload.firstName} ${action.payload.lastName}`
        }
      })
      .addCase(refreshAuthToken.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err)
      })
      .addCase(setPolicyAcceptance.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(setPolicyAcceptance.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.user = {
          ...state.user,
          ...action.payload,
          fullName: `${action.payload.firstName} ${action.payload.lastName}`
        }
      })
      .addCase(setPolicyAcceptance.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err)
      })
  }
})

export const selectAuth = (state: RootState) => state.auth

export const selectAuthStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectAuth, authState => authState.status)

export const getUserId: (state: RootState) => string = createSelector(
  selectAuth,
  pathOr('', ['user', 'id'])
)
export const getUserToken: (state: RootState) => string = createSelector(
  selectAuth,
  pathOr('', ['user', 'accessToken'])
)
export const getUserFullName: (state: RootState) => string = createSelector(
  selectAuth,
  pathOr('', ['user', 'fullName'])
)
export const getUserEmail: (state: RootState) => string = createSelector(
  selectAuth,
  pathOr('', ['user', 'email'])
)
export const getFullUser: (state: RootState) => IUserInfo = createSelector(
  selectAuth,
  authSlice => authSlice.user
)
export const getUserCountryPermissions: (state: RootState) => string[] =
  createSelector(
    selectAuth,
    pipe(
      pathOr([], ['user', 'countryPermissions']),
      map((el: ICountryPermissions) => el.country)
    )
  )
export const getUserCountryPermissionsSelectF: (
  state: RootState
) => IOptionSelectF[] = createSelector(
  selectAuth,
  pipe(
    pathOr([], ['user', 'countryPermissions']),
    map((el: ICountryPermissions) => ({
      label: el.country,
      name: el.country,
      value: el.country,
      id: el.id
    }))
  )
)
export const getUserDefaultCountry: (state: RootState) => string =
  createSelector(selectAuth, pathOr('', ['user', 'defaultCountry']))

export const getUserRole: (state: RootState) => IRole = createSelector(
  selectAuth,
  pathOr(
    {
      id: '',
      name: '',
      rolePermissions: [],
      createdAt: '',
      updatedAt: '',
      deletedAt: null
    },
    ['user', 'role']
  )
)

export const selectUserPermissions: (
  state: RootState
) => Record<string, boolean> = createSelector(
  selectAuth,
  pathOr({}, ['userPermissions'])
)

export default authSlice.reducer
